import { ConditionComparatorEnum, WhereClauseCondition } from "../ars_ldm";
import { AnnotationVariable } from "../contexts/interface";
import { comparatorDefinitions } from "./parseConditionFromCode";

/**
 * Formats a where clause condition as code for compact display.
 * Uses the variable's dataType to determine whether values should be quoted.
 * Numeric types are not quoted, text types are quoted.
 *
 * @param condition - The where clause condition to format.
 * @param availableVariables - List of available variables with dataType information.
 * @returns Formatted string like "ADSL.AGE = 18" (numeric) or "ADSL.SAFFL = 'Y'" (text).
 */
export const formatConditionAsCode = (
  condition: WhereClauseCondition | undefined,
  availableVariables?: AnnotationVariable[],
): string => {
  if (!condition || !condition.dataset || !condition.variable) {
    return "";
  }

  const dataset = condition.dataset;
  const variable = condition.variable;
  // Determine comparator enum (default to EQ)
  const compEnum = condition.comparator ?? ConditionComparatorEnum.EQ;
  // Default to empty array when value is undefined (provably unreachable in normal operation)
  const values =
    condition.value ??
    // istanbul ignore next
    [];

  const displayComparator = comparatorDefinitions[compEnum]
    ? comparatorDefinitions[compEnum].write
    : compEnum;

  if (values.length === 0) {
    return `${dataset}.${variable} ${displayComparator} ?`;
  }

  // Find the variable in the available list to determine its dataType
  const variableKey = `${dataset}.${variable}`;
  const variableInfo = availableVariables?.find((v) => v.value === variableKey);
  const isNumeric =
    variableInfo?.dataType?.toLowerCase() === "integer" ||
    variableInfo?.dataType?.toLowerCase() === "float" ||
    variableInfo?.dataType?.toLowerCase() === "numeric" ||
    variableInfo?.dataType?.toLowerCase() === "number" ||
    (!variableInfo && !isNaN(Number(values[0]))); // Fallback: if variable info is not available, check if value is numeric

  /**
   * Formats a single value with or without quotes based on dataType.
   *
   * @param v - The value to format.
   * @returns The formatted value.
   */
  const formatValue = (v: string): string => {
    return isNumeric ? v : `'${v}'`;
  };

  // Format values based on comparator
  if (compEnum === ConditionComparatorEnum.IN || compEnum === ConditionComparatorEnum.NOTIN) {
    const formattedValues = values.map(formatValue).join(", ");
    const compString = compEnum === ConditionComparatorEnum.NOTIN ? "NOT IN" : "IN";
    return `${dataset}.${variable} ${compString} (${formattedValues})`;
  }

  // Single value comparators
  const value = values[0];
  return `${dataset}.${variable} ${displayComparator} ${formatValue(value)}`;
};
